import { type ColumnDef, createColumnHelper } from "@tanstack/react-table"

export interface CouponRow {
  id: string
  code: string
  discount_type: "percent" | "free"
  discount_value: number
  max_uses: number | null
  times_used: number
  expires_at: string | null
  status: "active" | "expired" | "exhausted"
  scope: string
}

const col = createColumnHelper<CouponRow>()

type PushEvent = (event: string, payload: object) => void

export function makeCouponColumns(pushEvent: PushEvent): ColumnDef<CouponRow, any>[] {
  return [
    col.accessor("code", {
      header: "Code",
      cell: (info) => (
        <div className="flex items-center gap-2">
          <span className="font-mono text-sm font-semibold text-foreground tracking-widest">
            {info.getValue()}
          </span>
          <button
            onClick={() => navigator.clipboard.writeText(info.getValue())}
            className="inline-flex h-6 px-1.5 rounded text-[0.65rem] text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
            title="Copy code"
          >
            ⧉
          </button>
        </div>
      ),
    }),
    col.display({
      id: "discount",
      header: "Discount",
      cell: (info) => {
        const row = info.row.original
        return (
          <span className="inline-flex items-center h-6 px-2 rounded-md bg-primary/10 text-primary text-[0.75rem] font-semibold tabular-nums">
            {row.discount_type === "free" ? "100% FREE" : `${row.discount_value}% off`}
          </span>
        )
      },
    }),
    col.display({
      id: "uses",
      header: "Uses",
      cell: (info) => {
        const row = info.row.original
        const max = row.max_uses
        const used = row.times_used
        const pct = max ? Math.round((used / max) * 100) : null
        return (
          <div className="min-w-[80px]">
            <p className="text-xs text-foreground tabular-nums">
              {used}{max != null ? ` / ${max}` : ""}
            </p>
            {pct != null && (
              <div className="mt-1 h-1 w-16 bg-secondary rounded-full overflow-hidden">
                <div className="h-full bg-primary rounded-full" style={{ width: `${pct}%` }} />
              </div>
            )}
          </div>
        )
      },
    }),
    col.accessor("expires_at", {
      header: "Expires",
      cell: (info) => (
        <span className="text-xs text-muted-foreground tabular-nums">
          {info.getValue() || "Never"}
        </span>
      ),
    }),
    col.accessor("scope", {
      header: "Scope",
      cell: (info) => (
        <span className="text-xs text-muted-foreground capitalize">{info.getValue()}</span>
      ),
    }),
    col.accessor("status", {
      header: "Status",
      cell: (info) => {
        const s = info.getValue()
        const config = {
          active: "bg-emerald-500/10 text-emerald-500 border-emerald-500/20",
          expired: "bg-secondary text-muted-foreground border-border",
          exhausted: "bg-amber-500/10 text-amber-500 border-amber-500/20",
        } as const
        return (
          <span className={`inline-flex items-center text-[0.7rem] font-medium px-2 py-0.5 rounded-md border capitalize ${config[s as keyof typeof config] ?? config.expired}`}>
            {s}
          </span>
        )
      },
    }),
    col.display({
      id: "actions",
      header: "Actions",
      size: 140,
      cell: (info) => {
        const row = info.row.original
        return (
          <div className="flex items-center gap-2">
            {row.status === "active" && (
              <button
                onClick={() => {
                  if (confirm(`Deactivate coupon ${row.code}?`)) {
                    pushEvent("deactivate_coupon", { id: row.id })
                  }
                }}
                className="inline-flex items-center h-7 px-2.5 rounded-md text-[0.7rem] font-medium text-destructive hover:bg-destructive/10 transition-colors"
              >
                Deactivate
              </button>
            )}
            <button
              onClick={() => {
                if (confirm(`Delete coupon ${row.code}?`)) {
                  pushEvent("delete_coupon", { id: row.id })
                }
              }}
              className="inline-flex items-center h-7 px-2.5 rounded-md text-[0.7rem] font-medium text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
            >
              Delete
            </button>
          </div>
        )
      },
    }),
  ]
}
